/**
 * SuggestionFormDialog — the "New Suggestion" modal used from both
 * the user-facing "My Suggestions" page and the super_admin
 * "Improvement Suggestions" surface.
 *
 * Visible to every authenticated user regardless of role.  Backend
 * endpoint POST /api/suggestions is auth-only, no capability gate.
 */
import { useState } from "react";
import { toast } from "sonner";

import { api, formatApiError } from "../../lib/api";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";


const CATEGORIES = [
    { value: "bug", label: "Bug — something is broken" },
    { value: "improvement", label: "Improvement — a workflow could be better" },
    { value: "feature", label: "Feature request — something new" },
];


export default function SuggestionFormDialog({ open, onOpenChange, onCreated }) {
    const [title, setTitle] = useState("");
    const [category, setCategory] = useState("improvement");
    const [description, setDescription] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const reset = () => {
        setTitle("");
        setCategory("improvement");
        setDescription("");
    };

    const canSubmit =
        title.trim().length >= 5 &&
        description.trim().length >= 20 &&
        !submitting;

    const submit = async () => {
        if (!canSubmit) return;
        setSubmitting(true);
        try {
            const r = await api.post("/suggestions", {
                title: title.trim(),
                category,
                description: description.trim(),
            });
            toast.success(`Suggestion submitted — ${r.data.id}`);
            reset();
            onOpenChange(false);
            onCreated?.(r.data);
        } catch (err) {
            toast.error(formatApiError(err) || "Failed to submit suggestion");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!submitting) onOpenChange(v); }}>
            <DialogContent
                className="sm:max-w-lg"
                data-testid="suggestion-form-dialog"
            >
                <DialogHeader>
                    <DialogTitle>Suggest an Improvement</DialogTitle>
                    <DialogDescription>
                        Report a bug, suggest an improvement, or request a new
                        feature.  A super admin will review and respond.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-2">
                    <div className="space-y-1.5">
                        <Label htmlFor="sgn-title">Title</Label>
                        <Input
                            id="sgn-title"
                            data-testid="sgn-title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="One-line summary (e.g. Add dark mode to Leads screen)"
                            maxLength={200}
                        />
                        <p className="text-xs text-muted-foreground">
                            {title.trim().length}/200 — minimum 5 characters
                        </p>
                    </div>

                    <div className="space-y-1.5">
                        <Label htmlFor="sgn-category">Category</Label>
                        <Select value={category} onValueChange={setCategory}>
                            <SelectTrigger
                                id="sgn-category"
                                data-testid="sgn-category-trigger"
                            >
                                <SelectValue placeholder="Select…" />
                            </SelectTrigger>
                            <SelectContent>
                                {CATEGORIES.map((c) => (
                                    <SelectItem
                                        key={c.value}
                                        value={c.value}
                                        data-testid={`sgn-category-option-${c.value}`}
                                    >
                                        {c.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="space-y-1.5">
                        <Label htmlFor="sgn-description">Description</Label>
                        <Textarea
                            id="sgn-description"
                            data-testid="sgn-description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Tell us what you saw, what you expected, and why it matters.  Screenshots can be attached later in the review thread."
                            rows={6}
                            maxLength={2000}
                        />
                        <p className="text-xs text-muted-foreground">
                            {description.trim().length}/2000 — minimum 20 characters
                        </p>
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={submitting}
                        data-testid="sgn-cancel-btn"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={submit}
                        disabled={!canSubmit}
                        data-testid="sgn-submit-btn"
                    >
                        {submitting ? "Submitting…" : "Submit"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
